import React, { createContext, useContext, useEffect, useReducer, useRef } from "react";
import reducer from "./reducer";
import { adminSupabase as supabase } from "../../../client/adminSupabaseClient";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import getSellerData from "../../../helpers/getSellerData";

const initialState = {
    isLoading: true,
    isLoadingMore: false,
    showChatList: false,
    chatRooms: [],
    activeChatRoom: null,
    isSending: false
};

const ChatContext = createContext(undefined);

const AdminChatProvider = ({ children }) => {
    const { sellerId, userInfo: authUser, role } = useSelector((state) => state.auth);
    const [state, dispatch] = useReducer(reducer, initialState);
    const activeChatRoomIdRef = useRef(null);

    useEffect(() => {
        if (state?.activeChatRoom?.id) {
            activeChatRoomIdRef.current = state?.activeChatRoom?.id;
        }
    }, [state?.activeChatRoom?.id]);

    const loadChatRoomData = async (chatRooms) => {
        return Promise.all(
            chatRooms.map(async (chatRoom) => {
                const seller = await getSellerData(chatRoom.seller_id);

                const admin = {
                    id: 1,
                    name: "NEON MALL Seller Support",
                    image: "/logo.png",
                    type: "admin"
                };

                const isSenderSeller = Number(sellerId) === Number(chatRoom.seller_id);
                return {
                    ...chatRoom,
                    sender: isSenderSeller ? seller : admin,
                    receiver: isSenderSeller ? admin : seller
                };
            })
        );
    };

    //local actions
    const setIsLoading = (value) => {
        dispatch({ type: "SET_IS_LOADING", payload: value });
    };

    const setIsLoadingMore = (value) => {
        dispatch({ type: "SET_IS_LOADING_MORE", payload: value });
    };

    const setShowChatList = (show) => {
        dispatch({ type: "TOGGLE_CHAT_LIST", payload: show });
    };

    const setChatRooms = (chatRooms) => {
        dispatch({ type: "SET_CHAT_ROOMS", payload: chatRooms });
        const newActiveChatRoom = chatRooms.find((e) => e.id === state.activeChatRoom?.id);
        if (newActiveChatRoom) {
            dispatch({ type: "SET_ACTIVE_CHAT_ROOM", payload: newActiveChatRoom });
        }
    };

    const changeChatRoom = async (chatRoom) => {
        dispatch({
            type: "SET_ACTIVE_CHAT_ROOM",
            payload: chatRoom
        });
    };

    const appendMessages = async (appendTo, chatRoomId, newMessages, pagination = {}) => {
        const targetChatRoom = state.chatRooms.find((chatRoom) => chatRoom.id === chatRoomId);

        if (targetChatRoom) {
            const updatedChatRoom = {
                ...targetChatRoom,
                ...pagination,
                messages:
                    appendTo === "start"
                        ? [...newMessages, ...targetChatRoom.messages]
                        : [...targetChatRoom.messages, ...newMessages]
            };

            const newChatRooms = state.chatRooms.map((chatRoom) =>
                chatRoom.id === chatRoomId ? updatedChatRoom : chatRoom
            );

            // Update local state with newly new message
            dispatch({ type: "SET_CHAT_ROOMS", payload: newChatRooms });
            if (activeChatRoomIdRef.current === chatRoomId) {
                dispatch({ type: "SET_ACTIVE_CHAT_ROOM", payload: updatedChatRoom });
            }
        }
        // eslint-disable-next-line no-undef
        return Promise.resolve();
    };

    const insertOrUpdateLocalChatRoom = async (newChatRoom) => {
        const updatedChatRoomsWithData = await loadChatRoomData([newChatRoom]);
        const updatedChatRoomWithData = updatedChatRoomsWithData[0];
        const chatRoomExists = state.chatRooms.some(
            (oldChatRoom) => oldChatRoom.id === updatedChatRoomWithData.id
        );

        const newChatRooms = chatRoomExists
            ? state.chatRooms.map((oldChatRoom) =>
                  oldChatRoom.id === updatedChatRoomWithData.id
                      ? updatedChatRoomWithData
                      : oldChatRoom
              )
            : [...state.chatRooms, updatedChatRoomWithData];

        dispatch({ type: "SET_CHAT_ROOMS", payload: newChatRooms });
    };

    //supabase db actions
    const createNewChatRoom = async (sellerId) => {
        const { data, error } = await supabase
            .from("chat_rooms")
            .insert([
                {
                    seller_id: sellerId
                }
            ])
            .select("*, messages (*)");

        if (error) {
            throw new Error("Error creating new chat room");
        }

        const newChatRoom = data[0];
        const newChatRoomsWithData = await loadChatRoomData([newChatRoom]);
        const newChatRoomWithData = newChatRoomsWithData[0];

        dispatch({ type: "ADD_NEW_CHAT_ROOM", payload: newChatRoomWithData });
        return Promise.resolve(newChatRoomWithData);
    };

    const deleteChatRoom = async (chatRoomId) => {
        const { error } = await supabase.from("chat_rooms").delete().eq("id", chatRoomId);

        if (error) {
            toast.error("Error deleting chatroom", error);
            throw error;
        } else {
            const newChatRooms = state.chatRooms.filter((e) => e.id !== chatRoomId);
            dispatch({ type: "SET_CHAT_ROOMS", payload: newChatRooms });
            dispatch({ type: "SET_ACTIVE_CHAT_ROOM", payload: null });
            return Promise.resolve();
        }
    };

    const sendMessage = async (mediaType, content, senderId) => {
        const { activeChatRoom } = state;
        if (!authUser || !activeChatRoom?.id || !content) {
            return;
        }
        dispatch({ type: "SET_IS_SENDING", payload: true });

        try {
            const { data, error } = await supabase
                .from("messages")
                .insert({
                    room_id: activeChatRoom.id,
                    media_type: mediaType,
                    content,
                    sender_id: senderId ? senderId : role === "ROLE_ADMIN" ? 1 : sellerId
                })
                .select();

            if (error) {
                toast.error("Error sending new message:", error);
                throw new Error(`Error inserting message: ${error.message}`);
            }

            return data ? data[0] : null;
        } catch (error) {
            toast.error("Error sending new message:", error);
            throw error;
        } finally {
            dispatch({ type: "SET_IS_SENDING", payload: false });
        }
    };

    return (
        <ChatContext.Provider
            value={{
                ...state,
                setIsLoading,
                setIsLoadingMore,
                setShowChatList,
                setChatRooms,
                changeChatRoom,
                appendMessages,
                insertOrUpdateLocalChatRoom,

                //server actions
                loadChatRoomData,
                createNewChatRoom,
                deleteChatRoom,
                sendMessage
            }}
        >
            {children}
        </ChatContext.Provider>
    );
};

export const useAdminChat = () => {
    const context = useContext(ChatContext);
    if (!context) {
        throw new Error("useAdminChat must be used within a AdminChatProvider");
    }
    return context;
};

export default AdminChatProvider;
